import { Button, Callout, Classes, Intent, ProgressBar } from '@blueprintjs/core';
import { Classes as Classes2, Popover2 } from '@blueprintjs/popover2';
import cx from 'classnames';
import React, { useMemo } from 'react';

import Toolbar from '@f/components/shared/toolbar/Toolbar';
import { formatBytes } from '@f/utils/text';
import { useConfig } from '@f/hooks';

import cls from './StartingMaterialsPage.module.css';
import UploadForm from './components/UploadForm';
import DatasetList from './items/DatasetList';
import TemplateCreator from './items/TemplateCreator';

const USAGE_PERCENT_WARNING_THRESHOLD = 85;

function StartingMaterialsPage() {
  const [selectedItemIds, setSelectedItemIds] = React.useState<string[]>([]);
  const [isPopupOpen, setPopupOpen] = React.useState(false);
  const [isTemplateCreatorOpen, setTemplateCreatorOpen] = React.useState(false);
  const [usageInBytes, setUsageInBytes] = React.useState(0);

  const config = useConfig();
  const { datasetSizeLimitPerUpload } = config.startingMaterial;
  const MAX_SIZE_FORMATTED = formatBytes(datasetSizeLimitPerUpload, { unit: 'GB' });

  const usagePercent = useMemo(() => {
    const usagePercent = (usageInBytes / datasetSizeLimitPerUpload) * 100;
    const usagePercentRounded = Number(usagePercent.toFixed(2));
    return usagePercentRounded;
  }, [usageInBytes]);

  const onPopupOpen = () => {
    setPopupOpen(true);
  };

  const onPopupClose = () => {
    setPopupOpen(false);
  };

  const openTemplateCreator = (): void => {
    setTemplateCreatorOpen(true);
  };

  const closeTemplateCreator = (): void => {
    setTemplateCreatorOpen(false);
  };

  const onSelectionChange = (itemIds: string[]): void => {
    setSelectedItemIds(itemIds);
  };

  const remainingUsage = datasetSizeLimitPerUpload - usageInBytes;

  return (
    <>
      <Toolbar>
        <Toolbar.Title>Starting materials</Toolbar.Title>
        <Toolbar.Item alignRight light className={cls.firstToolbarItem}>
          <div className={cls.limitProgress}>
            Used {usagePercent}% of {MAX_SIZE_FORMATTED}
            <ProgressBar
              intent={usagePercent > USAGE_PERCENT_WARNING_THRESHOLD ? Intent.DANGER : Intent.PRIMARY}
              animate={false}
              value={usagePercent / 100}
              className={cls.progressBar}
            />
          </div>
          <Popover2
            usePortal
            isOpen={isPopupOpen}
            onClose={onPopupClose}
            content={
              <UploadForm
                maxUploadSize={remainingUsage}
                submitButtonProps={{
                  className: cls.uploadSubmitButton,
                  text: 'Start upload',
                }}
                onSubmit={onPopupClose}
              >
                <>
                  <h5 className={cx(Classes.HEADING, cls.uploadFormHeading)}>Upload starting material dataset</h5>
                  <Callout intent={Intent.PRIMARY}>
                    Configure and download the dataset template using{' '}
                    <Button minimal icon="th" intent={Intent.PRIMARY} small onClick={openTemplateCreator}>
                      template creator
                    </Button>
                  </Callout>
                  <TemplateCreator isOpen={isTemplateCreatorOpen} onClose={closeTemplateCreator} />
                </>
              </UploadForm>
            }
            placement="bottom-start"
            popoverClassName={Classes2.POPOVER2_CONTENT_SIZING}
          >
            <Button
              large
              disabled={usagePercent >= 100}
              active={isPopupOpen}
              intent={Intent.PRIMARY}
              text="Upload dataset"
              icon="cloud-upload"
              onClick={onPopupOpen}
            />
          </Popover2>
        </Toolbar.Item>
      </Toolbar>
      <div className={cls.page}>
        <DatasetList onSelectionChange={onSelectionChange} onDatasetsTotalSizeChange={setUsageInBytes} />
      </div>
    </>
  );
}

export default StartingMaterialsPage;
